import React from "react";
import { useTranslation } from "react-i18next";
import { SketchPicker } from "react-color";

import { ResetIcon } from "@components/Common/SvgIcons/ResetIcon";

import {
    Card,
    SectionHeader,
    CardTitle,
    ResetButton,
    ColorRow,
    ColorLabel,
    ColorSwatch,
    ColorValue,
    PopoverContainer,
    PopoverCover,
} from "./styled";
import { ICON_SIZE_SMALL } from "../shared/constants";

type ColorsCardProps = {
    mainColor: string;
    primaryColor: string;
    secondaryColor: string;
    showPrimaryPicker: boolean;
    showSecondaryPicker: boolean;
    onTogglePrimaryPicker: () => void;
    onToggleSecondaryPicker: () => void;
    onPrimaryChange: (color: string) => void;
    onSecondaryChange: (color: string) => void;
    onClosePrimaryPicker: () => void;
    onCloseSecondaryPicker: () => void;
    onResetColors: () => void;
};

export function ColorsCard({
    mainColor,
    primaryColor,
    secondaryColor,
    showPrimaryPicker,
    showSecondaryPicker,
    onTogglePrimaryPicker,
    onToggleSecondaryPicker,
    onPrimaryChange,
    onSecondaryChange,
    onClosePrimaryPicker,
    onCloseSecondaryPicker,
    onResetColors,
}: ColorsCardProps): JSX.Element {
    const { t } = useTranslation();

    return (
        <Card data-testid="scan-to-misc-colors-card">
            <SectionHeader>
                <CardTitle>{t("Colors")}</CardTitle>
                <ResetButton
                    data-testid="scan-to-misc-reset-colors-btn"
                    mainColor={mainColor}
                    onClick={onResetColors}
                    title={t("Reset colors")}
                    aria-label={t("Reset colors")}
                >
                    <ResetIcon style={{ width: ICON_SIZE_SMALL, height: ICON_SIZE_SMALL }} />
                </ResetButton>
            </SectionHeader>

            <ColorRow data-testid="scan-to-misc-primary-color-row">
                <ColorLabel>{t("Primary color")}</ColorLabel>
                <div style={{ position: "relative" }}>
                    <ColorSwatch
                        data-testid="scan-to-misc-primary-color-swatch"
                        color={primaryColor}
                        onClick={onTogglePrimaryPicker}
                    />
                    {showPrimaryPicker && (
                        <PopoverContainer>
                            <PopoverCover onClick={onClosePrimaryPicker} />
                            <SketchPicker
                                color={primaryColor}
                                onChangeComplete={(color) =>
                                    onPrimaryChange(color.hex)
                                }
                            />
                        </PopoverContainer>
                    )}
                </div>
                <ColorValue>{primaryColor}</ColorValue>
            </ColorRow>

            <ColorRow data-testid="scan-to-misc-secondary-color-row">
                <ColorLabel>{t("Secondary color")}</ColorLabel>
                <div style={{ position: "relative" }}>
                    <ColorSwatch
                        data-testid="scan-to-misc-secondary-color-swatch"
                        color={secondaryColor}
                        onClick={onToggleSecondaryPicker}
                    />
                    {showSecondaryPicker && (
                        <PopoverContainer>
                            <PopoverCover onClick={onCloseSecondaryPicker} />
                            <SketchPicker
                                color={secondaryColor}
                                onChangeComplete={(color) =>
                                    onSecondaryChange(color.hex)
                                }
                            />
                        </PopoverContainer>
                    )}
                </div>
                <ColorValue>{secondaryColor}</ColorValue>
            </ColorRow>
        </Card>
    );
}
